import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader, Shield, User, AlertCircle, BookOpen } from 'lucide-react';
import ArticleModal from './ArticleModal';

const CITATION_REGEX = /\[(?:Article\s+)?([A-ZÇ][A-Z0-9-]{2,}(?:-\d+)*)\]/g;

export default function AIChat({ userId, API_URL }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Bonjour ! Je suis votre assistant juridique. Posez-moi une question sur le Code du Travail : mes réponses sont ancrées sur les articles officiels Légifrance, cliquables à côté de chaque affirmation.' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeArticle, setActiveArticle] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeArticle]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    setMessages(prev => [...prev, { role: 'assistant', content: '', sources: [] }]);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Erreur d'authentification. Veuillez vous reconnecter.");

      const response = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ question: userMsg.content })
      });

      if (!response.ok) throw new Error("Erreur connexion IA");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const event of events) {
          if (event.startsWith('data: ')) {
            try {
              const data = JSON.parse(event.slice(6));

              if (data.type === 'token') {
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    lastMsg.content += data.content;
                  }
                  return newMsgs;
                });
              } else if (data.type === 'sources') {
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    lastMsg.sources = Array.isArray(data.sources) ? data.sources : [];
                  }
                  return newMsgs;
                });
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              console.error("SSE Parse Error:", e);
            }
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: `Erreur: ${err.message}` }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderContent = (msg) => {
    const content = msg.content;
    if (!content) return null;

    const parts = [];
    let last = 0;
    let m;
    let key = 0;

    CITATION_REGEX.lastIndex = 0;
    while ((m = CITATION_REGEX.exec(content)) !== null) {
      if (m.index > last) parts.push(content.slice(last, m.index));
      const num = m[1];
      const src = Array.isArray(msg.sources) ? msg.sources.find(s => s && String(s.num) === num) : null;
      if (src) {
        parts.push(
          <button
            key={`cite-${key++}`}
            onClick={() => setActiveArticle(src)}
            title={`Article ${num} — voir le texte officiel`}
            className="inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded font-semibold text-xs align-baseline cursor-pointer"
          >
            {num} <BookOpen size={10} />
          </button>
        );
      } else {
        parts.push(`[${num}]`);
      }
      last = m.index + m[0].length;
    }
    if (last < content.length) parts.push(content.slice(last));
    return parts.length > 0 ? parts : content;
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 p-4 overflow-y-auto space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm ${msg.role === 'user'
              ? 'bg-emerald-600 text-white rounded-br-none'
              : msg.role === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-white text-black border border-gray-200 rounded-bl-none'
              }`}>
              <div className="whitespace-pre-wrap">{renderContent(msg)}</div>
              {msg.sources && msg.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-1.5">
                    Sources officielles — cliquez pour le texte exact
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {msg.sources.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveArticle(s)}
                        title={s.section ? `${s.section} — article ${s.num}` : `Article ${s.num}`}
                        className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-full font-semibold flex items-center gap-1"
                      >
                        <BookOpen size={11} /> {s.num ? `A. ${s.num}` : 'Source'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 flex items-center gap-2 text-gray-800 font-medium text-xs">
              <Loader className="w-3 h-3 animate-spin" /> Recherche des articles officiels...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t bg-white">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-400">
          <Shield size={12} className="text-emerald-500" />
          <span>Réponses ancrées sur les articles Légifrance. Cliquez sur une source ou une citation pour vérifier le texte officiel.</span>
        </div>
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Posez une question juridique..."
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm text-black font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {activeArticle && (
        <ArticleModal article={activeArticle} API_URL={API_URL} onClose={() => setActiveArticle(null)} />
      )}
    </div>
  );
}